import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const FaceDescriptor = () => {
    const [descriptors, setDescriptors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        emp_id: '',
        capture_type: '',
        is_active: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        emp_id: '',
        capture_type: '',
        is_active: '',
        search: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        employees: [],
        capture_types: []
    });

    const searchInputRef = useRef(null);
    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchDescriptors = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            // Build params object with only non-empty values from appliedFilters
            const params = {};
            if (appliedFilters.emp_id) params.emp_id = appliedFilters.emp_id;
            if (appliedFilters.capture_type) params.capture_type = appliedFilters.capture_type;
            if (appliedFilters.is_active !== '') params.is_active = appliedFilters.is_active;
            if (appliedFilters.search) params.search = appliedFilters.search;

            const response = await axios.get(`${API_BASE}facedescriptor/get_facedescriptor.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: params,
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setDescriptors(response.data.data || []);
                // Set filter options if available
                if (response.data.filters) {
                    setFilterOptions({
                        employees: response.data.filters.employees || [],
                        capture_types: response.data.filters.capture_types || []
                    });
                }
                setError('');
            } else {
                setDescriptors([]);
                setError(response.data?.error || 'Failed to fetch face descriptors');
            }
        } catch (error) {
            setDescriptors([]);
            const errorMsg = error.response?.data?.error || error.message || 'Failed to fetch face descriptors';
            setError(errorMsg);
            console.error('Error fetching face descriptors:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, appliedFilters]);

    useEffect(() => {
        fetchDescriptors();
    }, [fetchDescriptors]);

    const handleDelete = async (descriptorId) => {
        if (!window.confirm('Are you sure you want to delete this face descriptor? Attendance relying on this descriptor might be affected.')) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}facedescriptor/delete_facedescriptor.php`,
                { descriptor_id: descriptorId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data && response.data.success) {
                // Remove from current UI state
                setDescriptors(prev => prev.filter(desc => desc.descriptor_id !== descriptorId));
                alert('Face descriptor deleted successfully.');
            } else {
                alert(response.data?.error || 'Failed to delete face descriptor');
            }
        } catch (error) {
            console.error('Error deleting face descriptor:', error);
            alert(error.response?.data?.error || error.message || 'Failed to delete descriptor');
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({
            emp_id: filters.emp_id,
            capture_type: filters.capture_type,
            is_active: filters.is_active,
            search: searchTerm
        });
    };

    const handleClearFilters = () => {
        setFilters({
            emp_id: '',
            capture_type: '',
            is_active: ''
        });
        setSearchTerm('');
        setAppliedFilters({
            emp_id: '',
            capture_type: '',
            is_active: '',
            search: ''
        });
        // Keep focus on search input after clearing
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleRefresh = () => {
        fetchDescriptors();
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'N/A';
        const date = new Date(dateTime);
        return date.toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    const getCaptureTypeBadge = (captureType) => {
        const badgeClasses = {
            'REGISTRATION': 'bg-primary',
            'UPDATE': 'bg-warning',
            'AUTO': 'bg-secondary'
        };
        return badgeClasses[captureType] || 'bg-light text-dark';
    };

    const getStatusBadge = (isActive) => {
        return isActive === 1 ? 'bg-success' : 'bg-danger';
    };

    if (loading && descriptors.length === 0) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3 text-muted">Loading face descriptors...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3">
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/dashboard')} title="Dashboard">
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/Menu')} title="Menu">
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-person-bounding-box me-2 text-primary"></i>
                                                Face Descriptors
                                            </h2>
                                            <p className="text-muted mb-0">View & manage employee face descriptors</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <button className="btn btn-primary" onClick={handleRefresh}>
                                        <i className="bi bi-arrow-clockwise me-2"></i> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span><strong>Error:</strong> {error}</span>
                    </div>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setError('')}>
                        Dismiss
                    </button>
                </div>
            )}

            {/* Filters Card */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Search</label>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Employee name, code or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilters();
                                    }
                                }}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Employee</label>
                            <select
                                className="form-select"
                                name="emp_id"
                                value={filters.emp_id}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Employees</option>
                                {filterOptions.employees.map(emp => (
                                    <option key={emp.emp_id} value={emp.emp_id}>
                                        {emp.emp_code} - {emp.first_name} {emp.last_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Capture Type</label>
                            <select
                                className="form-select"
                                name="capture_type"
                                value={filters.capture_type}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Types</option>
                                {filterOptions.capture_types.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Status</label>
                            <select
                                className="form-select"
                                name="is_active"
                                value={filters.is_active}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-12 d-flex justify-content-end">
                            <button className="btn btn-outline-secondary me-2" onClick={handleClearFilters}>
                                <i className="bi bi-x-circle me-1"></i> Clear Filters
                            </button>
                            <button className="btn btn-primary" onClick={handleApplyFilters}>
                                <i className="bi bi-search me-1"></i> Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Filters Display */}
            {(appliedFilters.search || appliedFilters.emp_id || appliedFilters.capture_type || appliedFilters.is_active) && (
                <div className="alert alert-info mb-4 py-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <div>
                            <i className="bi bi-funnel-fill me-2"></i>
                            <strong>Active Filters:</strong>
                            {appliedFilters.search && <span className="badge bg-primary ms-2">Search: {appliedFilters.search}</span>}
                            {appliedFilters.emp_id && filterOptions.employees.find(e => e.emp_id === parseInt(appliedFilters.emp_id)) &&
                                <span className="badge bg-secondary ms-2">
                                    Employee: {filterOptions.employees.find(e => e.emp_id === parseInt(appliedFilters.emp_id)).first_name}
                                </span>
                            }
                            {appliedFilters.capture_type && <span className="badge bg-secondary ms-2">Type: {appliedFilters.capture_type}</span>}
                            {appliedFilters.is_active && <span className="badge bg-secondary ms-2">Status: {appliedFilters.is_active === '1' ? 'Active' : 'Inactive'}</span>}
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                            Clear All
                        </button>
                    </div>
                </div>
            )}

            {/* Results Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4" style={{ width: '80px' }}>ID</th>
                                    <th>Employee</th>
                                    <th style={{ width: '140px' }}>Capture Type</th>
                                    <th style={{ width: '100px' }}>Status</th>
                                    <th style={{ width: '100px' }}>Quality Score</th>
                                    <th style={{ width: '180px' }}>Created At</th>
                                    <th className="text-end pe-4" style={{ width: '100px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {descriptors.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <i className="bi bi-inbox fs-1 text-muted"></i>
                                            <p className="text-muted mt-2 mb-0">No face descriptors found</p>
                                        </td>
                                    </tr>
                                ) : (
                                    descriptors.map(descriptor => (
                                        <tr key={descriptor.descriptor_id}>
                                            <td className="ps-4 fw-semibold">#{descriptor.descriptor_id}</td>
                                            <td>
                                                <div className="fw-semibold">{descriptor.emp_code || `EMP-${descriptor.emp_id}`}</div>
                                                <small className="text-muted">
                                                    {descriptor.first_name} {descriptor.last_name}
                                                    {descriptor.email && <span className="ms-1">({descriptor.email})</span>}
                                                </small>
                                            </td>
                                            <td>
                                                <span className={`badge ${getCaptureTypeBadge(descriptor.capture_type)}`}>
                                                    {descriptor.capture_type || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(descriptor.is_active)}`}>
                                                    {descriptor.is_active === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                {descriptor.quality_score ?
                                                    <span className="fw-semibold">{descriptor.quality_score}</span> :
                                                    <span className="text-muted">-</span>
                                                }
                                            </td>
                                            <td>
                                                <div className="small">{formatDateTime(descriptor.created_at)}</div>
                                                {descriptor.updated_at && (
                                                    <div className="small text-muted">
                                                        Updated: {formatDateTime(descriptor.updated_at)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-end pe-4">
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(descriptor.descriptor_id)}
                                                    title="Delete Face Descriptor"
                                                >
                                                    <i className="bi bi-trash me-1"></i> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Summary */}
            {descriptors.length > 0 && (
                <div className="mt-3 text-end text-muted small">
                    Showing {descriptors.length} face descriptor(s)
                </div>
            )}
        </div>
    );
};

export default FaceDescriptor;